import { useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageWrapper } from '../components/layout/PageWrapper';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';
import { useAuthStore } from '../store/authStore';
import { isFreeAdmin } from '../lib/freeAdmins';
import { api } from '../lib/apiClient';
import {
  useFinanceDay,
  useUpsertPlayerPayment,
  useCreateExpense,
  useUpdateExpense,
  useDeleteExpense,
} from '../hooks/useFinance';
import type { PaymentMethod, FinanceExpense, BalancePool } from '../types/finance';

const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  online: 'Онлайн',
  cash: 'Нал',
  bank: 'Банк',
  unpaid: 'Не оплатил',
};

const BALANCE_LABELS: Record<BalancePool, string> = {
  online: 'Онлайн',
  card: 'Карта',
  cash: 'Нал',
};

function formatRuFull(iso: string) {
  const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
  const [y, m, d] = iso.split('-');
  return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
}

function PlayerPaymentRow({
  player,
  date,
  gameExpenses,
}: {
  player: {
    playerKey: string; displayName: string; isAdmin?: boolean; stripeAmount?: number;
    paymentId?: string; paymentMethod?: PaymentMethod; paymentAmount?: number;
    isReimbursement?: boolean; reimbursesExpenseId?: string; reimbursesAmount?: number; note?: string;
  };
  date: string;
  gameExpenses: FinanceExpense[];
}) {
  const upsert = useUpsertPlayerPayment(date);
  const isStripe = player.stripeAmount !== undefined && !player.paymentId;
  const [method, setMethod] = useState<PaymentMethod>(player.paymentMethod ?? 'unpaid');
  const [amount, setAmount] = useState(String(player.paymentAmount ?? ''));
  const [isReimb, setIsReimb] = useState(player.isReimbursement ?? false);
  const [reimburseExpId, setReimburseExpId] = useState(player.reimbursesExpenseId ?? '');
  const [note, setNote] = useState(player.note ?? '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await upsert.mutateAsync({
        playerKey: player.playerKey,
        payload: {
          displayName: player.displayName,
          paymentMethod: method,
          amountAud: parseFloat(amount) || 0,
          isReimbursement: isReimb,
          ...(isReimb && reimburseExpId ? { reimbursesExpenseId: reimburseExpId } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      });
    } finally {
      setSaving(false);
    }
  }

  if (player.isAdmin) {
    return (
      <div className="flex items-center gap-3 py-3 px-4 border-b border-mafia-border last:border-b-0">
        <p className="flex-1 text-mafia-paper text-sm font-medium">{player.displayName}</p>
        <span className="text-xs font-mono px-2 py-0.5 bg-mafia-gold/10 text-mafia-gold border border-mafia-gold/40">Админ</span>
      </div>
    );
  }

  if (isStripe) {
    return (
      <div className="flex items-center gap-3 py-3 px-4 border-b border-mafia-border last:border-b-0">
        <p className="flex-1 text-mafia-paper text-sm font-medium">{player.displayName}</p>
        <span className="text-xs font-mono px-2 py-0.5 bg-green-900/40 text-green-400 border border-green-800">Онлайн ✓</span>
        <p className="text-mafia-paper text-sm font-bold w-16 text-right">${(player.stripeAmount ?? 0).toFixed(2)}</p>
      </div>
    );
  }

  return (
    <div className="py-3 px-4 border-b border-mafia-border last:border-b-0">
      <div className="flex items-center gap-3">
        <p className="flex-1 text-mafia-paper text-sm font-medium">{player.displayName}</p>
        <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}
          className="bg-mafia-dark border border-mafia-border text-mafia-paper text-xs p-1.5 focus:outline-none focus:border-mafia-gold">
          {(Object.keys(PAYMENT_LABELS) as PaymentMethod[]).map((m) => (
            <option key={m} value={m}>{PAYMENT_LABELS[m]}</option>
          ))}
        </select>
        <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
          placeholder="$0"
          className="w-20 bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-1.5 text-right focus:outline-none focus:border-mafia-gold" />
        <button type="button" onClick={handleSave} disabled={saving}
          className="btn-gold-solid px-3 py-1.5 text-xs disabled:opacity-50">
          {saving ? '...' : 'Сохранить'}
        </button>
      </div>
      {(method === 'cash' || method === 'bank') && (
        <div className="mt-2 ml-0 flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-mafia-muted font-mono cursor-pointer">
            <input type="checkbox" checked={isReimb} onChange={(e) => setIsReimb(e.target.checked)} />
            Возмещение расхода
          </label>
          {isReimb && gameExpenses.length > 0 && (
            <select value={reimburseExpId} onChange={(e) => setReimburseExpId(e.target.value)}
              className="bg-mafia-dark border border-mafia-border text-mafia-paper text-xs p-1 focus:outline-none focus:border-mafia-gold">
              <option value="">— выберите расход —</option>
              {gameExpenses.map((exp) => (
                <option key={exp.entryId} value={exp.entryId}>{exp.name} (${exp.amountAud})</option>
              ))}
            </select>
          )}
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Примечание"
            className="flex-1 min-w-32 bg-mafia-dark border border-mafia-border text-mafia-paper text-xs p-1 focus:outline-none focus:border-mafia-gold" />
        </div>
      )}
    </div>
  );
}

function ReceiptUploader({ expense, onUploaded }: { expense: FinanceExpense; onUploaded: () => void }) {
  const updateExpense = useUpdateExpense();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [viewUrl, setViewUrl] = useState<string | null>(null);

  async function handleFile(file: File) {
    setUploading(true);
    try {
      const { uploadUrl, key } = await api.get<{ uploadUrl: string; key: string }>(
        `/finance/receipts/upload-url?filename=${encodeURIComponent(file.name)}`,
      );
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      await updateExpense.mutateAsync({ entryId: expense.entryId, payload: { pk: expense.pk, sk: expense.sk, receiptKey: key } });
      onUploaded();
    } finally {
      setUploading(false);
    }
  }

  async function handleView() {
    if (!expense.receiptKey) return;
    const { viewUrl: url } = await api.get<{ viewUrl: string }>(
      `/finance/receipts/view-url?key=${encodeURIComponent(expense.receiptKey)}`,
    );
    setViewUrl(url);
    window.open(url, '_blank');
  }

  if (expense.receiptKey) {
    return (
      <button type="button" onClick={handleView}
        className="text-mafia-muted hover:text-mafia-gold text-xs font-mono underline underline-offset-2 transition-colors">
        {viewUrl ? 'Открыть чек' : 'Чек ↗'}
      </button>
    );
  }

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*,.pdf" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
        className="text-mafia-muted hover:text-mafia-gold text-xs font-mono underline underline-offset-2 transition-colors disabled:opacity-50">
        {uploading ? 'Загрузка...' : '+ Чек'}
      </button>
    </>
  );
}

function ExpenseRow({ expense, date, onReimburse }: { expense: FinanceExpense; date: string; onReimburse: (e: FinanceExpense) => void }) {
  const deleteExpense = useDeleteExpense(date);
  const updateExpense = useUpdateExpense(date);
  const totalReimbursed = expense.reimbursements.reduce((s, r) => s + r.amount, 0);
  const fullyReimbursed = totalReimbursed >= expense.amountAud - 0.005;

  if (expense.auto) {
    return (
      <div className="flex items-center gap-3 py-3 px-4 border-b border-mafia-border last:border-b-0 opacity-60">
        <p className="flex-1 text-mafia-paper-dim text-sm italic">{expense.name}</p>
        <p className="text-mafia-paper-dim text-sm font-bold w-16 text-right">${expense.amountAud.toFixed(2)}</p>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 py-3 px-4 border-b border-mafia-border last:border-b-0">
      <div className="flex-1">
        <p className="text-mafia-paper text-sm font-medium">{expense.name}</p>
        {totalReimbursed > 0 && (
          <p className={`text-xs font-mono ${fullyReimbursed ? 'text-green-400' : 'text-mafia-gold'}`}>
            возмещено ${totalReimbursed.toFixed(2)} {fullyReimbursed ? '✓' : `/ $${expense.amountAud.toFixed(2)}`}
          </p>
        )}
      </div>
      <ReceiptUploader expense={expense} onUploaded={() => updateExpense.mutate({ entryId: expense.entryId, payload: { pk: expense.pk, sk: expense.sk } })} />
      {!fullyReimbursed && (
        <button type="button" onClick={() => onReimburse(expense)}
          className="text-mafia-muted hover:text-mafia-gold text-xs font-mono underline underline-offset-2 transition-colors">
          Возместить
        </button>
      )}
      <p className="text-mafia-paper text-sm font-bold w-16 text-right">${expense.amountAud.toFixed(2)}</p>
      <button type="button" onClick={() => deleteExpense.mutate({ entryId: expense.entryId, pk: expense.pk, sk: expense.sk })}
        className="text-mafia-muted hover:text-mafia-crimson-light text-xs font-mono transition-colors">
        ✕
      </button>
    </div>
  );
}

function ReimburseModal({ expense, date, onClose }: { expense: FinanceExpense; date: string; onClose: () => void }) {
  const updateExpense = useUpdateExpense(date);
  const totalReimbursed = expense.reimbursements.reduce((s, r) => s + r.amount, 0);
  const outstanding = expense.amountAud - totalReimbursed;
  const [from, setFrom] = useState<BalancePool>('card');
  const [amount, setAmount] = useState(String(outstanding.toFixed(2)));
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amtNum = parseFloat(amount);
    if (isNaN(amtNum) || amtNum <= 0) { setError('Введите корректную сумму'); return; }
    try {
      await updateExpense.mutateAsync({
        entryId: expense.entryId,
        payload: {
          pk: expense.pk, sk: expense.sk,
          addReimbursement: { amount: amtNum, from, date: new Date().toISOString().split('T')[0], ...(note.trim() ? { note: note.trim() } : {}) },
        },
      });
      onClose();
    } catch {
      setError('Не удалось добавить возмещение');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-mafia-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="card-vintage w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-mafia-paper font-display text-xl font-bold mb-1">Возместить расход</h2>
        <p className="text-mafia-paper-dim text-sm mb-4">{expense.name} — к возврату: ${outstanding.toFixed(2)}</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-mafia-muted text-xs font-mono">Из счёта</label>
            <select value={from} onChange={(e) => setFrom(e.target.value as BalancePool)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1">
              {(['online', 'card', 'cash'] as BalancePool[]).map((p) => <option key={p} value={p}>{BALANCE_LABELS[p]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-mafia-muted text-xs font-mono">Сумма возмещения, AUD</label>
            <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1 focus:outline-none focus:border-mafia-gold" />
          </div>
          <div>
            <label className="text-mafia-muted text-xs font-mono">Примечание</label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1 focus:outline-none focus:border-mafia-gold" />
          </div>
          {error && <p className="text-mafia-crimson-light text-xs font-mono">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={updateExpense.isPending} className="btn-gold-solid px-4 py-2 text-sm flex-1 disabled:opacity-50">
              {updateExpense.isPending ? '...' : 'Возместить'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-mafia-border text-mafia-paper-dim hover:text-mafia-paper transition-colors">
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddGameExpenseForm({ date, onAdded }: { date: string; onAdded: () => void }) {
  const createExpense = useCreateExpense();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amtNum = parseFloat(amount);
    if (!name.trim()) { setError('Введите название'); return; }
    if (isNaN(amtNum) || amtNum <= 0) { setError('Введите сумму'); return; }
    try {
      await createExpense.mutateAsync({ type: 'game', date, name: name.trim(), amountAud: amtNum });
      setName(''); setAmount('');
      onAdded();
    } catch {
      setError('Не удалось добавить расход');
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 flex-wrap px-4 py-3 border-t border-mafia-border">
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Название расхода"
        className="flex-1 min-w-40 bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 focus:outline-none focus:border-mafia-gold" />
      <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="$"
        className="w-24 bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 text-right focus:outline-none focus:border-mafia-gold" />
      <button type="submit" disabled={createExpense.isPending} className="btn-gold-solid px-4 py-2 text-sm disabled:opacity-50">
        {createExpense.isPending ? '...' : '+ Добавить'}
      </button>
      {error && <p className="w-full text-mafia-crimson-light text-xs font-mono">{error}</p>}
    </form>
  );
}

export function AdminFinanceDayPage() {
  const { user } = useAuthStore();
  const { date } = useParams<{ date: string }>();
  const { data, isLoading } = useFinanceDay(date ?? '');
  const [reimburseExpense, setReimburseExpense] = useState<FinanceExpense | null>(null);

  if (!isFreeAdmin(user?.email)) {
    return <PageWrapper><p className="text-mafia-paper-dim">Нет доступа.</p></PageWrapper>;
  }

  return (
    <PageWrapper>
      {reimburseExpense && (
        <ReimburseModal expense={reimburseExpense} date={date!} onClose={() => setReimburseExpense(null)} />
      )}

      <Link to="/admin/finance" className="font-mono text-xs text-mafia-muted hover:text-mafia-gold tracking-wider uppercase transition-colors mb-8 inline-block">
        ← Финансы
      </Link>

      <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">◆ Админ ◆</p>
      <h1 className="font-display text-4xl font-bold text-mafia-paper mb-8">
        {date ? formatRuFull(date) : '—'}
      </h1>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-mafia-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !data ? (
        <p className="text-mafia-paper-dim font-mono text-sm">Данные не найдены.</p>
      ) : (
        <>
          {/* Players */}
          <h2 className="text-mafia-paper text-xl font-bold mb-3">Игроки</h2>
          <div className="card-vintage mb-8">
            {data.players.length === 0 ? (
              <p className="text-mafia-paper-dim text-sm font-mono px-4 py-3">Нет данных об игроках.</p>
            ) : (
              data.players.map((player) => (
                <PlayerPaymentRow key={player.playerKey} player={player} date={date!} gameExpenses={data.gameExpenses} />
              ))
            )}
          </div>

          <OrnamentDivider className="mb-8" />

          {/* Game expenses */}
          <h2 className="text-mafia-paper text-xl font-bold mb-3">Расходы на игру</h2>
          <div className="card-vintage mb-8">
            {data.gameExpenses.length === 0 ? (
              <p className="text-mafia-paper-dim text-sm font-mono px-4 py-3">Нет расходов.</p>
            ) : (
              data.gameExpenses.map((exp) => (
                <ExpenseRow key={exp.entryId} expense={exp} date={date!} onReimburse={setReimburseExpense} />
              ))
            )}
            <AddGameExpenseForm date={date!} onAdded={() => {}} />
          </div>

          <OrnamentDivider className="mb-8" />

          {/* Day summary */}
          <h2 className="text-mafia-paper text-xl font-bold mb-4">Итоги дня</h2>
          <div className="card-vintage p-5 space-y-2">
            {[
              { label: 'Собрано онлайн', value: data.summary.onlineTotal, color: 'text-mafia-paper' },
              { label: 'Собрано наличными', value: data.summary.cashTotal, color: 'text-mafia-paper' },
              { label: 'Собрано банком', value: data.summary.bankTotal, color: 'text-mafia-paper' },
              { label: 'Расходы на игру', value: data.summary.expenseTotal, color: 'text-mafia-crimson-light' },
              { label: 'Возмещено расходов', value: data.summary.reimbursedTotal, color: 'text-green-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-mafia-muted font-mono">{label}</span>
                <span className={`font-bold ${color}`}>${value.toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t border-mafia-border pt-2 flex justify-between text-sm">
              <span className="text-mafia-paper font-mono font-medium">Итого собрано</span>
              <span className="text-mafia-gold font-bold">
                ${(data.summary.onlineTotal + data.summary.cashTotal + data.summary.bankTotal).toFixed(2)}
              </span>
            </div>
          </div>
        </>
      )}
    </PageWrapper>
  );
}
